import { Controller } from "@hotwired/stimulus";
import axios from "axios";
import { toggleSections } from "../section_appearance";
import * as Sentry from "@sentry/browser";

export default class InitialProjectData extends Controller {
  static values = {
    voucherId: Number,
    packageId: Number,
    serviceIds: Array,
  };

  initialize() {
    this.countryCode = this.data.get("countryCode");
    this.locale = this.data.get("locale");
    this.professionalId = this.data.get("professionalId");
    this.packageID = this.data.get("packageId");
    this.auth = this.data.get("auth");
    this.projectType = this.data.get("projectType");
  }

  connect() {
    // fetch the selected professional
    if (this.professionalId) {
      this.getProfessional();
    }

    // preselect the package if the user start the project from a package
    if (this.packageID) {
      this.preSelectPackage(this.packageID);
    }
  }

  async getProfessional() {
    try {
      await axios({
        method: "get",
        url: `/${this.countryCode}/${this.locale}/p/${this.professionalId}`,
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html, application/json",
        },
      }).then((response) => {
        // to render turbo response when use axios we have to call renderStreamMessage
        Turbo.renderStreamMessage(response.data);
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // === Voucher ===
  async submitCode() {
    const discountCode = document.getElementById("discount-input").value;
    try {
      const voucherID = await this.checkDiscountCodeValidity(
        discountCode,
        this.professionalId,
        this.auth,
      );
      if (voucherID) {
        this.voucherIdValue = voucherID;
      }
    } catch (error) {
      console.error("Error submitting discount code:", error);
    }
  }

  async fetchVoucherValidation(discountCode, professional_id, auth) {
    try {
      return await axios({
        method: "post",
        url: `/${this.countryCode}/${this.locale}/vouchers/validate_voucher`,
        data: {
          voucher: {
            discount_code: discountCode,
            professional_id,
          },
        },
        headers: {
          "X-CSRF-Token": auth,
          Accept: "text/vnd.turbo-stream.html, text/html, application/json",
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  async checkDiscountCodeValidity(discountCode, professional_id, auth) {
    try {
      const response = await this.fetchVoucherValidation(
        discountCode,
        professional_id,
        auth,
      );
      // extract the voucher ID from the turbo response
      const voucherIdMatch = response.data.match(/data-voucher-id="([^"]*)"/);
      const voucherId = voucherIdMatch ? parseInt(voucherIdMatch[1]) : null;
      // render the Turbo Stream message
      await Turbo.renderStreamMessage(response.data);
      return voucherId;
    } catch (error) {
      console.error("Error validating discount code:", error);
      return null;
    }
  }

  removeVoucher() {
    this.voucherIdValue = null;
    document.getElementById("discount-input").value = null; // reset the input feild
    toggleSections(false, "voucher-code-is-valid", "discount-code-error");
    toggleSections(true, "voucher-not-applied");
  }

  // === Services ===
  setServices(event) {
    const serviceID = parseInt(
      event.target.closest("[data-initial-project-data-service]")?.dataset
        .initialProjectDataService,
    );
    if (event.target.checked) {
      this.serviceIdsValue = [...this.serviceIdsValue, serviceID];
    } else {
      this.serviceIdsValue = this.serviceIdsValue.filter(
        (id) => id !== serviceID,
      );
    }
  }

  // === Packages ===
  preSelectPackage(packageId) {
    const radioId = `radio-${packageId}`;
    this.switchToPackages();

    const attemptSelect = () => {
      const radio = document.getElementById(radioId);
      if (radio) {
        radio.checked = true;
        this.packageIdValue = packageId;
        this.setCarouselToPackage(packageId);
        observer.disconnect(); // stop once found
      }
    };

    // MutationObserver provides the ability to watch for changes being made to the DOM
    const observer = new MutationObserver(() => {
      attemptSelect();
    });

    observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }
  setCarouselToPackage(packageId) {
    // Find the carousel item that contains this package
    const packageCard = document.querySelector(
      `[data-initial-project-data-package="${packageId}"]`,
    );

    if (packageCard) {
      const carouselItem = packageCard.closest(".carousel-item");

      if (carouselItem) {
        // Get all carousel items to find the index
        const allCarouselItems = document.querySelectorAll(
          "#carouselExampleIndicators .carousel-item",
        );
        const slideIndex = Array.from(allCarouselItems).indexOf(carouselItem);

        if (slideIndex >= 0) {
          // Use Bootstrap 4 jQuery carousel API
          $("#carouselExampleIndicators").carousel(slideIndex);
        }
      }
    }
  }

  switchToPackages() {
    // Preselect the "Packages" pill/tab
    const packagesTab = document.getElementById("pills-packages-tab");
    if (packagesTab && !packagesTab.classList.contains("active")) {
      packagesTab.classList.add("active");
      packagesTab.setAttribute("aria-selected", "true");

      // Deactivate the "Services" tab
      const servicesTab = document.getElementById("pills-services-tab");
      if (servicesTab) {
        servicesTab.classList.remove("active");
        servicesTab.setAttribute("aria-selected", "false");
      }

      // Show the packages tab pane, hide the services tab pane
      const packagesPane = document.getElementById("pills-packages");
      const servicesPane = document.getElementById("pills-services");
      if (packagesPane) {
        packagesPane.classList.add("show", "active");
      }
      if (servicesPane) {
        servicesPane.classList.remove("show", "active");
      }
    }
  }
  setPackage(event) {
    // Prevent triggering when clicking on interactive elements
    if (event.target.closest("a, button, input, label")) {
      console.log(
        "canceled early, if radio button will be handled on its own, else clicked on view package",
      );
      return;
    }

    const card = event.target.closest("[data-initial-project-data-package]");
    const packageID = parseInt(card?.dataset.initialProjectDataPackage);
    const radioInput = card.querySelector(
      ".initial-project-data-package-radio-field",
    );

    if (!radioInput || !packageID) return;

    if (radioInput.checked && this.packageIdValue == packageID) {
      // Toggle off if clicking the same package
      radioInput.checked = false;
      this.packageIdValue = null;
    } else {
      // Select this package - browser automatically unchecks others due to name attribute
      radioInput.checked = true;
      this.packageIdValue = packageID;
    }
  }

  // Add this as a new action in your HTML
  setPackageFromRadio(event) {
    event.stopPropagation(); // This should prevent bubbling to the card

    const radioInput = event.target;
    const packageID = parseInt(event.target.value);

    if (this.packageIdValue == packageID) {
      // unchecking
      radioInput.checked = false;
      this.packageIdValue = null;
    } else {
      // checking
      radioInput.checked = true;
      this.packageIdValue = packageID;
    }
  }

  // === Submit ===
  async submitInitialData(event) {
    event.preventDefault();
    const title = document.getElementById("direct_project_title").value;
    const url = this.setUrlAndParams(title);

    if (this.isError(title)) {
      try {
        const response = await axios.get(url.toString(), {
          headers: { Accept: "text/vnd.turbo-stream.html" },
        });
        Turbo.renderStreamMessage(response.data);
      } catch (error) {
        Sentry.captureException(error);
      }
    } else {
      // rails will render the full view
      Turbo.visit(url.toString());
    }
  }

  isError(title) {
    !title ||
      ((this.packageIdValue == 0 || !this.packageIdValue) &&
        this.serviceIdsValue.length == 0);
  }

  setUrlAndParams(title) {
    const url = new URL(
      `/${this.countryCode}/${this.locale}/hire/questionnaire`,
      window.location.origin,
    );

    url.searchParams.append("data[title]", title);
    url.searchParams.append("data[type]", this.projectType);
    url.searchParams.append("data[professional_id]", this.professionalId);
    if (this.voucherIdValue)
      url.searchParams.append("data[voucher_id]", this.voucherIdValue);
    if (this.packageIdValue)
      url.searchParams.append("data[package_id]", this.packageIdValue);
    this.serviceIdsValue.forEach((id) =>
      url.searchParams.append("data[service_ids][]", id),
    );

    return url;
  }
}
